/**
 * <gift-with-cart> custom element
 *
 * Adds a free gift to the cart whenever an eligible trigger product is present,
 * and removes it when no trigger products remain.
 *
 * Loop-safe and duplicate-safe:
 *  - Requests issued by this component are tagged and ignored by the fetch patch.
 *  - Reconcile does not enqueue another reconcile.
 *  - Optimistic add is deduped and gated on a persistent "gift present" flag,
 *    so it fires at most once per gift-presence cycle.
 */

class GiftWithCart extends HTMLElement {
  constructor() {
    super();
    this.giftVariantId = Number(this.dataset.giftVariantId);
    this.giftPropertyKey = this.dataset.giftPropertyKey || '_gift';
    this.giftMaxQty = Number(this.dataset.giftMaxQty) || 1;
    // RAUVE 2026-10-20: free charm is a tier reward (2+ paid pieces), not $50+
    this.minTriggerQty = Number(this.dataset.minTriggerQty) || 1;
    this.triggerHandles = this.dataset.triggerHandles
      ? this.dataset.triggerHandles.split(',').filter(Boolean)
      : null;

    this.cartCache = null;
    this.cartCacheAt = 0;
    this.CACHE_TTL = 1500;

    this.syncPromise = null;
    this.needsResync = false;
    this.giftAddInFlight = null;

    this.internalOps = 0;

    // ---- Duplicate-add guards ----
    // Set true the moment we add the gift OR observe it in a cart response.
    // Cleared only when a reconcile confirms the gift is no longer present.
    this.giftPresent = false;
    // Line key of the gift item, if known. Useful for removals/edits.
    this.giftLineKey = null;
  }

  connectedCallback() {
    document.addEventListener('cart:updated', this.#onCartUpdated);
    this.patchFetch();
    this.sync({ immediate: true });
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.#onCartUpdated);
    this.internalOps = 0;
    this.giftAddInFlight = null;
  }

  #onCartUpdated = () => {
    if (this.internalOps > 0) return;
    this.cartCache = null;
    this.sync();
  };

  async #cartFetch(url, init) {
    this.internalOps++;
    try {
      return await window.__giftOriginalFetch(url, init);
    } finally {
      this.internalOps--;
    }
  }

  patchFetch() {
    if (window.__giftFetchPatched) return;
    window.__giftFetchPatched = true;

    const originalFetch = window.fetch.bind(window);
    window.__giftOriginalFetch = originalFetch;

    const getInstance = () => document.querySelector('gift-with-cart');

    window.fetch = async (input, init) => {
      const url = typeof input === 'string' ? input : input?.url || '';
      const method = (init?.method || input?.method || 'GET').toUpperCase();

      const response = await originalFetch(input, init);

      const instance = getInstance();
      if (!instance) return response;
      if (instance.internalOps > 0) return response;

      if (response.ok && method === 'POST' && url.includes('/cart/add')) {
        if (instance.minTriggerQty > 1) {
          // RAUVE 2026-10-20: tier-gated gift, so reconcile against the real cart instead of adding blindly
          instance.cartCache = null;
          queueMicrotask(() => instance.sync());
        } else {
          instance.optimisticGiftAdd();
        }
      } else if (
        response.ok &&
        method === 'POST' &&
        (url.includes('/cart/change') || url.includes('/cart/update'))
      ) {
        // If the theme or the customer modified the cart, our cached "present"
        // state may no longer hold. Invalidate cache but DO NOT clear giftPresent
        // here — only a fresh reconcile can do that, after seeing real cart data.
        instance.cartCache = null;
        queueMicrotask(() => instance.sync());
      }

      return response;
    };
  }

  optimisticGiftAdd() {
    // ---- GUARD 1: gift already known to be present ----
    if (this.giftPresent) return null;

    // ---- GUARD 2: an add is already in flight ----
    if (this.giftAddInFlight) return this.giftAddInFlight;

    // ---- GUARD 3: cached cart shows the gift at/above max ----
    if (this.cartCache) {
      const giftQty = this.cartCache.items
        .filter((i) => this.isGiftItem(i))
        .reduce((sum, i) => sum + i.quantity, 0);
      if (giftQty >= this.giftMaxQty) {
        this.giftPresent = true;
        return null;
      }
    }

    // Mark present immediately so any concurrent trigger bails out.
    this.giftPresent = true;

    this.giftAddInFlight = this.#cartFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: this.giftVariantId,
            quantity: 1,
            properties: { [this.giftPropertyKey]: 'true' },
          },
        ],
      }),
    })
      .then((res) => {
        if (!res.ok) {
          // Add failed — let the next reconcile try again.
          this.giftPresent = false;
        } else {
          this.cartCache = null;
          this.#emitCartUpdated();
        }
      })
      .catch((err) => {
        console.warn('[gift-with-cart] optimistic add failed:', err);
        this.giftPresent = false;
      })
      .finally(() => {
        this.giftAddInFlight = null;
      });

    return this.giftAddInFlight;
  }

  async sync({ immediate = false } = {}) {
    if (this.syncPromise) {
      this.needsResync = true;
      return this.syncPromise;
    }

    this.syncPromise = (async () => {
      do {
        this.needsResync = false;
        const cart = await this.getCart({ immediate });
        await this.reconcile(cart);
      } while (this.needsResync);
    })().finally(() => {
      this.syncPromise = null;
    });

    return this.syncPromise;
  }

  async getCart({ immediate = false } = {}) {
    const now = Date.now();
    if (!immediate && this.cartCache && now - this.cartCacheAt < this.CACHE_TTL) {
      return this.cartCache;
    }
    const res = await this.#cartFetch('/cart.js', {
      headers: { Accept: 'application/json' },
    });
    const cart = await res.json();
    this.cartCache = cart;
    this.cartCacheAt = Date.now();
    return cart;
  }

  isGiftItem(item) {
    return (
      Number(item.variant_id) === this.giftVariantId ||
      (item.properties && item.properties[this.giftPropertyKey])
    );
  }

  isTriggerItem(item) {
    if (this.isGiftItem(item)) return false;
    if (!this.triggerHandles) return true;
    return this.triggerHandles.includes(item.handle);
  }

  async reconcile(cart) {
    const triggerItems = cart.items.filter((i) => this.isTriggerItem(i));
    const giftItems = cart.items.filter((i) => this.isGiftItem(i));

    // RAUVE 2026-10-20: an Edit full-set bundle counts as 2 pieces for the free charm
    const triggerQty = triggerItems.reduce((sum, i) => sum + i.quantity * (/-edit-full-set$/.test(i.handle || '') ? 2 : 1), 0);
    const shouldHaveGift = triggerQty >= this.minTriggerQty;
    const currentGiftQty = giftItems.reduce((sum, i) => sum + i.quantity, 0);

    // ---- Sync the persistent guard to ground truth ----
    if (currentGiftQty > 0) {
      this.giftPresent = true;
      this.giftLineKey = giftItems[giftItems.length - 1].key;
    } else {
      this.giftPresent = false;
      this.giftLineKey = null;
    }

    // Nothing to do.
    if (!shouldHaveGift && currentGiftQty === 0) return;
    if (shouldHaveGift && currentGiftQty === this.giftMaxQty) return;

    if (shouldHaveGift && currentGiftQty === 0) {
      await this.addGift();
    } else if (!shouldHaveGift && currentGiftQty > 0) {
      await this.removeGift(giftItems);
    } else if (shouldHaveGift && currentGiftQty > this.giftMaxQty) {
      const excess = currentGiftQty - this.giftMaxQty;
      const lineToReduce = giftItems[giftItems.length - 1];
      await this.changeLine(lineToReduce.key, lineToReduce.quantity - excess);
    }
  }

  async addGift() {
    // Same guard as the optimistic path — don't double-add.
    if (this.giftPresent && this.giftLineKey) return;

    const res = await this.#cartFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: this.giftVariantId,
            quantity: 1,
            properties: { [this.giftPropertyKey]: 'true' },
          },
        ],
      }),
    });
    if (res.ok) {
      this.giftPresent = true;
      this.cartCache = null;
      this.#emitCartUpdated();
    }
  }

  async removeGift(giftItems) {
    const updates = {};
    for (const item of giftItems) updates[item.key] = 0;
    const res = await this.#cartFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (res.ok) {
      this.giftPresent = false;
      this.giftLineKey = null;
      this.cartCache = null;
      this.#emitCartUpdated();
    }
  }

  async changeLine(key, quantity) {
    const res = await this.#cartFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (res.ok) {
      this.cartCache = null;
      this.#emitCartUpdated();
    }
  }

  #emitCartUpdated() {
    this.internalOps++;
    try {
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
    } finally {
      queueMicrotask(() => {
        this.internalOps = Math.max(0, this.internalOps - 1);
      });
    }
  }
}

if (!customElements.get('gift-with-cart')) {
  customElements.define('gift-with-cart', GiftWithCart);
}